<script lang="ts" setup>
import type { GameItem, Period } from '@kira-dancer/nuxt-common'
import { random, round } from 'lodash'
import { TOP_WINNERS_LIMIT } from '~/constants'
import { TopWinnerSectionTabsEnum, KeyEnum } from '~/enums'
import type {
  SummaryItem,
  TopWinningItem,
  TopWinningResponse,
} from '~/types/index'
import { maskString } from '~/utils/string'

const { fetchTopWinning } = useLobbyGames()
const { onPlayGame } = usePlayGame()
const tab = ref(TopWinnerSectionTabsEnum.Monthly)
const isFirstLoad = ref(true)

const fetchDataFetchTopWinning = async () => {
  const cachedData = cacheManager.get(KeyEnum.TopWinning)
  if (cachedData && isFirstLoad.value) {
    return cachedData
  }
  try {
    const data = await fetchTopWinning('', tab.value as unknown as Period)
    cacheManager.set(KeyEnum.TopWinning, data, 360)
    return data
  } catch {
    return []
  }
}

const { data: rawData, refresh } = useAsyncData(
  KeyEnum.TopWinning,
  fetchDataFetchTopWinning,
)
const { allGames } = useFetchGames()

onMounted(() => {
  tab.value = TopWinnerSectionTabsEnum.Weekly
})

const topWinners: ComputedRef<TopWinningItem[]> = computed(() => {
  if (!rawData.value) {
    return []
  }
  const response = rawData.value[0] as unknown as TopWinningResponse

  const data = response?.data
  if (!data) {
    return []
  }

  let summary = data.summary.slice(0, TOP_WINNERS_LIMIT)
  if (summary.length < TOP_WINNERS_LIMIT) {
    const fillCount = TOP_WINNERS_LIMIT - summary.length
    const fillData = Array.from({ length: fillCount }, () => ({
      username: '',
      amount: 0,
      uuid: '',
    }))
    summary = summary.concat(fillData)
  }

  return summary.map((item: SummaryItem, index: number): TopWinningItem => {
    const isWaitingItem = !item.username
    if (isWaitingItem) {
      return {
        rank: '-',
        username: 'Đang cập nhật',
        amount: '0',
      }
    }
    let game: GameItem | undefined

    if (allGames?.value && allGames.value.length) {
      const gameItems = allGames.value
      game = gameItems[random(0, gameItems.length - 1)]
    }

    return {
      rank: index + 1,
      username: item.username,
      amount: formatAmountUnit(round(item.amount), 1),
      game: game,
    }
  })
})

const tabItems = [
  {
    key: TopWinnerSectionTabsEnum.Weekly,
    title: 'Top xếp hạng tuần',
  },
  {
    key: TopWinnerSectionTabsEnum.Monthly,
    title: 'Top xếp hạng tháng',
  },
]

const getRankImage = (rank: string): string => {
  return `/images/home/top-winner/ic-rank-${rank}.webp`
}

watch(tab, () => {
  refresh()
  if (isFirstLoad.value) {
    isFirstLoad.value = false
    return
  }
})

const onClickGameItem = (game?: GameItem) => {
  if (game) {
    onPlayGame(game)
  }
}
</script>

<template>
  <div class="top-winner-section">
    <BaseSection id="top-winners" title="Top Winner" icon="icon-rank">
      <div v-if="topWinners?.length">
        <table>
          <thead>
            <tr>
              <th>Thứ hạng</th>
              <th>Người chơi</th>
              <th>Trò chơi</th>
              <th>Thắng cược</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in topWinners" :key="item.rank">
              <td>
                <img
                  v-if="[1, 2, 3].includes(Number(item.rank))"
                  class="top-winner-section__item-rank-img"
                  :src="getRankImage(item.rank.toString())"
                  :alt="`Rank ${item.rank}`"
                  width="38"
                  height="38"
                />
                <span v-else class="top-winner-section__item-rank-text">{{
                  item.rank
                }}</span>
              </td>
              <td>
                {{ maskString(item.username, 3, 8) + item.username.slice(-2) }}
              </td>
              <td>
                <div
                  class="top-winner-section__item-game"
                  :class="{
                    'top-winner-section__item-game--clickable': item.game,
                  }"
                  @click="onClickGameItem(item.game)"
                >
                  <ClientOnly>
                    <BaseImage
                      v-if="item.game"
                      :src="item.game.img?.thumbnail.url"
                      class="top-winner-section__item-game-img"
                      :alt="`Top winner ${item.game?.title}`"
                      loading="lazy"
                      width="40"
                      height="52"
                    />
                    <div
                      v-else
                      class="top-winner-section__item-game-img top-winner-section__item-game-img--no-image"
                    />
                    <span class="line-limit-2">{{
                      item.game?.title ?? 'Trò chơi'
                    }}</span>
                  </ClientOnly>
                </div>
              </td>
              <td>
                <div class="top-winner-section__item-coin-wrapper">
                  <div
                    class="top-winner-section__item-coin"
                    :class="
                      item.amount === '0' &&
                      'top-winner-section__item-coin--no-coin'
                    "
                  >
                    <ClientOnly>
                      <img
                        :class="
                          item.amount === '0' &&
                          'top-winner-section__item-coin-img--no-coin'
                        "
                        class="top-winner-section__item-coin-img"
                        src="/images/home/top-winner/ic-coin.png"
                        :alt="`Icon ${item.game?.title}`"
                        width="30"
                        height="30"
                      />
                    </ClientOnly>
                    <span class="top-winner-section__item-coin-value">{{
                      item.amount
                    }}</span>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="top-winner-section__empty">
        <div class="top-winner-section__empty-img">
          <img
            class="top-winner-section__empty-img"
            src="/images/home/top-winner/ic-empty.webp"
            alt="empty"
            width="100"
            height="100"
          />
        </div>
        <span class="top-winner-section__empty-text"
          >Hệ thống đang cập nhật, vui lòng thử lại sau</span
        >
      </div>
      <template #right-action>
        <BaseTabs v-model="tab" :tabs="tabItems" />
      </template>
    </BaseSection>
  </div>
</template>

<style scoped lang="scss">
@import '~/assets/styles/components/desktop/home/top-winner-section.scss';
</style>
